/**
 * Phase 66 Plan 02 (D-14) — `<Can permission="...">` RBAC wrapper.
 *
 * Renders children only when the active user has the given permission
 * (server-issued role_scopes ∪ ROLE_PERMISSIONS[role]). Returns null —
 * or the optional `fallback` ReactNode — otherwise.
 *
 * UI gating ONLY. Backend remains the authoritative validator on every
 * endpoint. T-66-02-03 (RBAC bypass via localStorage manipulation) is
 * mitigated by this principle — UI tampering reveals nothing the
 * backend doesn't already enforce.
 *
 * @example
 *   <Can permission="audit:read">
 *     <NavLink to="/admin/audit">Audit log</NavLink>
 *   </Can>
 *
 *   <Can permission="users:admin" fallback={<p>Accesso negato</p>}>
 *     <UsersAdminPanel />
 *   </Can>
 */

import type { ReactNode } from "react";

import { useCan } from "./useUser";
import type { Permission } from "./permissions";

export type CanProps = {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
};

export default function Can({ permission, children, fallback = null }: CanProps) {
  const can = useCan();
  if (!can(permission)) return <>{fallback}</>;
  return <>{children}</>;
}
